import { Amplify } from "aws-amplify";
import {
  signIn,
  signOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  updatePassword,
} from "aws-amplify/auth";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID as string,
      loginWith: {
        username: true,
      },
    },
  },
});

export interface AuthUser {
  userId: string;
  username: string;
  groups: string[];
  firstName?: string;
  lastName?: string;
  email?: string;
}

export async function login(username: string, password: string) {
  try {
    return await signIn({ username, password });
  } catch (err) {
    // Amplify throws UserAlreadyAuthenticatedException when a stale session
    // lingers (e.g. after a forced redirect to /login without signOut).
    // Sign out to clear local state, then retry once.
    if (err instanceof Error && err.name === "UserAlreadyAuthenticatedException") {
      await signOut();
      return await signIn({ username, password });
    }
    throw err;
  }
}

export async function logout() {
  await signOut();
}

export async function completeNewPassword(newPassword: string) {
  return confirmSignIn({ challengeResponse: newPassword });
}

export async function getCurrentAuthUser(): Promise<AuthUser | null> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    const attrs = await fetchUserAttributes();
    const groups = (session.tokens?.accessToken?.payload["cognito:groups"] as string[] | undefined) ?? [];

    return {
      userId: user.userId,
      username: user.username,
      groups,
      firstName: attrs.given_name,
      lastName: attrs.family_name,
      email: attrs.email,
    };
  } catch {
    return null;
  }
}

export async function getAccessToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: options?.forceRefresh ?? false });
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function forgotPassword(username: string) {
  return resetPassword({ username });
}

export async function confirmForgotPassword(username: string, code: string, newPassword: string) {
  return confirmResetPassword({ username, confirmationCode: code, newPassword });
}

export async function changeUserPassword(oldPassword: string, newPassword: string) {
  return updatePassword({ oldPassword, newPassword });
}

export function isAdmin(user: AuthUser | null): boolean {
  return user?.groups.includes("super-admin") ?? false;
}

export function isEmployee(user: AuthUser | null): boolean {
  return (
    (user?.groups.includes("normal_user") || user?.groups.includes("super-admin")) ?? false
  );
}
